import axios, { AxiosHeaders } from 'axios';

import type { AxiosResponse, RawAxiosHeaders } from 'axios';

/** One violation on a validation-error body: the member it is about and a code naming the rule. */
export type InvalidParam = { name: string; code: string };

export type AppErrorKind =
  | 'network'
  | 'timeout'
  | 'canceled'
  | 'auth'
  | 'forbidden'
  | 'notFound'
  | 'validation'
  | 'server'
  | 'unknown';

/**
 * The single error shape the application sees; nothing outside this module handles a raw
 * AxiosError. A real Error subclass, not a plain object: a non-Error throw loses the stack
 * trace, breaks `instanceof Error` in third-party code, and degrades what Sentry reports.
 */
export class AppError extends Error {
  readonly kind: AppErrorKind;
  readonly status?: number;
  /** Backend error code when the payload carries one; `client.`-prefixed when this client set it. */
  readonly code?: string;
  /**
   * The Problem payload's `detail` alone, when the server sent one. `message` falls back through
   * `title` and the transport's own wording, so it cannot tell a caller whether the server spoke;
   * a caller that wants to render the server's sentence and otherwise word the failure itself
   * reads this instead.
   */
  readonly detail?: string;
  /** The Problem payload's `title`, for callers that branch on which conflict the server named. */
  readonly title?: string;
  /** Field-level messages, fed straight into react-hook-form's setError. */
  readonly fieldErrors?: Record<string, string[]>;
  /**
   * RFC 9457 `invalid_params`: each violated member and its code, never the value sent. Codes,
   * not messages, so the caller words each one; kept apart from `fieldErrors` for that reason.
   */
  readonly invalidParams?: InvalidParam[];
  /** The last segment of the Problem `type` URI (`.../problems/invalid-for-state`), to branch on. */
  readonly problemType?: string;
  /** The Problem's `reason` extension, which narrows a `problemType` such as `invalid-for-state`. */
  readonly reason?: string;
  /** The Problem's `current_state` extension: the resource's state when the server refused. */
  readonly currentState?: string;
  /**
   * `Retry-After` in delta-seconds. Best effort: a browser reads the header only when the edge
   * lists it in `Access-Control-Expose-Headers`, so a caller must read correctly without it. The
   * HTTP-date form is not parsed.
   */
  readonly retryAfterSeconds?: number;
  readonly traceId?: string;
  /**
   * The id support uses to find this request in the backend's logs, safe to show the user: it
   * names the request, not its content. Absent when neither the body nor a readable header
   * carried one.
   */
  readonly correlationId?: string;

  constructor(init: {
    kind: AppErrorKind;
    message: string;
    status?: number;
    code?: string;
    detail?: string;
    title?: string;
    fieldErrors?: Record<string, string[]>;
    invalidParams?: InvalidParam[];
    problemType?: string;
    reason?: string;
    currentState?: string;
    retryAfterSeconds?: number;
    traceId?: string;
    correlationId?: string;
    /** Original error, preserved for Sentry. */
    cause?: unknown;
  }) {
    super(init.message, { cause: init.cause });
    this.name = 'AppError';
    this.kind = init.kind;
    this.status = init.status;
    this.code = init.code;
    this.detail = init.detail;
    this.title = init.title;
    this.fieldErrors = init.fieldErrors;
    this.invalidParams = init.invalidParams;
    this.problemType = init.problemType;
    this.reason = init.reason;
    this.currentState = init.currentState;
    this.retryAfterSeconds = init.retryAfterSeconds;
    this.traceId = init.traceId;
    this.correlationId = init.correlationId;
  }
}

/** RFC 7807-ish payload; every field is optional since the API's shape varies by endpoint. */
type ProblemPayload = {
  type?: string;
  title?: string;
  detail?: string;
  code?: string;
  traceId?: string;
  correlation_id?: string;
  /** Set on errors a backend service raised; RFC 9457 bodies carry this or `request_id`. */
  trace_id?: string;
  /** Set on errors the gateway raised itself (401, 403, throttling). */
  request_id?: string;
  errors?: Record<string, string[]>;
  invalid_params?: unknown;
  reason?: string;
  current_state?: string;
  message?: string;
};

/** Keeps only well-formed entries, so a malformed body cannot hand a caller a non-string code. */
function readInvalidParams(value: unknown): InvalidParam[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const params = value.filter(
    (item): item is InvalidParam =>
      typeof item === 'object' &&
      item !== null &&
      typeof (item as InvalidParam).name === 'string' &&
      typeof (item as InvalidParam).code === 'string',
  );
  return params.length > 0 ? params.map(({ name, code }) => ({ name, code })) : undefined;
}

function readProblemType(type: unknown): string | undefined {
  if (typeof type !== 'string') return undefined;
  const slug = type.slice(type.lastIndexOf('/') + 1);
  return slug === '' ? undefined : slug;
}

/**
 * API Gateway stamps every response with this id. A browser reads it only when the edge lists it
 * in `Access-Control-Expose-Headers`; otherwise axios sees no such header and this yields nothing.
 */
const REQUEST_ID_HEADER = 'x-amzn-requestid';

/** Case-insensitive, whether axios handed back an `AxiosHeaders` or a plain object. */
function readHeader(headers: AxiosResponse['headers'], name: string): string | undefined {
  // The response type's index signature is narrower in name only; at runtime both shapes are
  // what `from` takes.
  const value = AxiosHeaders.from(headers as RawAxiosHeaders).get(name);
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/** Delta-seconds only; a negative, fractional or HTTP-date value yields nothing. */
function readRetryAfter(headers: AxiosResponse['headers']): number | undefined {
  const value = readHeader(headers, 'retry-after');
  return value !== undefined && /^\d+$/.test(value.trim()) ? Number(value.trim()) : undefined;
}

function kindForStatus(status: number): AppErrorKind {
  if (status === 401) return 'auth';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'notFound';
  if (status === 422 || status === 400) return 'validation';
  if (status >= 500) return 'server';
  return 'unknown';
}

export function isAppError(value: unknown): value is AppError {
  return value instanceof AppError;
}

/**
 * Normalizes anything thrown by the HTTP layer into an AppError. Pure and free of
 * framework imports so it stays unit-testable and is the single seam to change if the
 * API's error shape changes.
 */
export function toAppError(error: unknown): AppError {
  if (isAppError(error)) return error;

  if (axios.isCancel(error)) {
    return new AppError({ kind: 'canceled', message: 'Request canceled', cause: error });
  }

  if (axios.isAxiosError(error)) {
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      return new AppError({ kind: 'timeout', message: 'The request timed out', cause: error });
    }

    // No response object means the request never completed — DNS failure,
    // offline, CORS rejection. Distinct from a 5xx, which did reach the server.
    if (!error.response) {
      return new AppError({ kind: 'network', message: 'Unable to reach the server', cause: error });
    }

    const status = error.response.status;
    const payload = (error.response.data ?? {}) as ProblemPayload;

    return new AppError({
      kind: kindForStatus(status),
      status,
      code: payload.code,
      detail: payload.detail,
      title: payload.title,
      message: payload.detail ?? payload.title ?? payload.message ?? error.message,
      fieldErrors: payload.errors,
      invalidParams: readInvalidParams(payload.invalid_params),
      problemType: readProblemType(payload.type),
      reason: typeof payload.reason === 'string' ? payload.reason : undefined,
      currentState: typeof payload.current_state === 'string' ? payload.current_state : undefined,
      retryAfterSeconds: readRetryAfter(error.response.headers),
      traceId: payload.trace_id ?? payload.traceId,
      // A body field is the server choosing what to show; the header is the gateway's fallback.
      correlationId:
        payload.correlation_id ??
        payload.trace_id ??
        payload.traceId ??
        payload.request_id ??
        readHeader(error.response.headers, REQUEST_ID_HEADER),
      cause: error,
    });
  }

  return new AppError({
    kind: 'unknown',
    message: error instanceof Error ? error.message : 'Something went wrong',
    cause: error,
  });
}
